import { BankHoliday } from "@jasper/client-models";
import { isBeforeDay } from "@jasper/shared";

export type DeliveryOption = "optimal" | "express" | "custom";

export const isSameDay = (a?: Date, b?: Date) =>
  !!a && !!b && a.toDateString() === b.toDateString();

// A date is express when it is earlier than the optimal one, compared day by day (time is ignored)
export const isExpressDate = (
  date: Date,
  estimatedOptimalDeliveryDate?: Date
) =>
  !!estimatedOptimalDeliveryDate &&
  isBeforeDay(date, estimatedOptimalDeliveryDate);

// e.g. "mer. 6 mai"
export const formatDeliveryDate = (date: Date) =>
  date.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });

export const getDeliveryOption = ({
  deliveryDate,
  estimatedOptimalDeliveryDate,
  estimatedRushDeliveryDate,
}: {
  deliveryDate: Date;
  estimatedOptimalDeliveryDate?: Date;
  estimatedRushDeliveryDate?: Date;
}): DeliveryOption => {
  if (isSameDay(deliveryDate, estimatedOptimalDeliveryDate)) {
    return "optimal";
  }

  if (isSameDay(deliveryDate, estimatedRushDeliveryDate)) {
    return "express";
  }

  return "custom";
};

export const DELIVERY_OPTION_LABELS: Record<DeliveryOption, string> = {
  optimal: "Date optimale",
  express: "Date express",
  custom: "Date choisie",
};

const isBankHoliday = (date: Date, bankHolidays: BankHoliday[]) =>
  bankHolidays.some(bankHoliday => {
    const start = new Date(bankHoliday.startDate);
    const end = new Date(bankHoliday.endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return date >= start && date <= end;
  });

// Weekdays that are not bank holidays, from tomorrow up to the given date included
export const countBusinessDaysUntil = (
  to: Date,
  bankHolidays: BankHoliday[]
) => {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  const target = new Date(to);
  target.setHours(0, 0, 0, 0);

  let count = 0;
  while (day < target) {
    day.setDate(day.getDate() + 1);
    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
    if (!isWeekend && !isBankHoliday(day, bankHolidays)) {
      count++;
    }
  }

  return count;
};
